import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { TokenResponse, LoginRequest, RegisterRequest, User, Role, Permission, Tab, PaginatedResponse, Ensayo, EnsayoMedia, EnsayoMediaList, ActuadorDataList, SensorList, Sensor, SensorDataList, SensorWithData, TipoSensor, Proyecto, FOSSensor, FOSSensorList, TimestampAdjustment, TimestampAdjustmentList, TimestampAdjustmentRequest, EnsayoSamplingInfo, ExportVariableSpec, ExportJobStartResponse, ExportJobStatusResponse, ScatterDisplayResponse, SensorDataBatchResponse } from '../types';
import { isPublicEndpoint, isPublicRoute } from '../constants/routes';
import {
  clearCsrfToken,
  addCsrfTokenToHeaders,
  updateCsrfTokenFromHeaders,
  getCsrfToken,
  isMutableMethod
} from '../utils/csrf';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Almacenar access token en memoria como fallback cuando las cookies no funcionan
// (especialmente en móviles con problemas de cookies cross-origin)
let accessTokenMemory: string | null = null;

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private refreshFailed = false; // Flag para evitar loops infinitos de refresh
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Importante: enviar cookies automáticamente
      timeout: 30000, // 30 segundos de timeout
    });

    // Interceptor para agregar token CSRF y access token en requests
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Asegurarse de que los headers existan
        if (!config.headers) {
          config.headers = {} as any;
        }
        
        // Agregar access token en header como fallback cuando las cookies no funcionan
        // Esto es especialmente útil en móviles donde las cookies cross-origin pueden no funcionar
        if (accessTokenMemory) {
          config.headers['Authorization'] = `Bearer ${accessTokenMemory}`;
        }
        
        // Agregar token CSRF en métodos mutables
        // Para FormData, axios puede eliminar headers, así que lo agregamos explícitamente
        if (isMutableMethod(config.method)) {
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            // Establecer el header directamente para asegurar que se envíe
            config.headers['X-CSRF-Token'] = csrfToken;
          } else {
            // Si no hay token CSRF, intentar obtenerlo automáticamente
            // Esto puede pasar al inicio de la aplicación antes de que se haya llamado a /auth/me
            // En lugar de mostrar un warning, intentamos obtenerlo silenciosamente
            // El token se obtendrá del header X-CSRF-Token de la próxima respuesta
            // Por ahora, no agregamos el header y dejamos que el backend maneje el error si es necesario
            // El interceptor de respuestas actualizará el token cuando llegue
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores 401 y 403
    this.api.interceptors.response.use(
      (response) => {
        // Actualizar token CSRF de los headers de respuesta
        updateCsrfTokenFromHeaders(response.headers);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // No intentar refresh en rutas públicas o endpoints que no requieren autenticación
        const isPublicApiEndpoint = originalRequest.url && isPublicEndpoint(originalRequest.url);
        
        // Verificar si estamos en una ruta pública del frontend
        const currentPath = window.location.pathname;
        const isPublicFrontendRoute = isPublicRoute(currentPath);
        
        // Manejar errores 429 (Too Many Requests) - no intentar refresh
        if (error.response?.status === 429) {
          // Si es un error de rate limit en refresh, marcar como fallido para evitar loops
          if (originalRequest.url?.includes('/auth/refresh')) {
            this.refreshFailed = true;
            clearCsrfToken();
          }
          // Rechazar el error sin intentar refresh
          return Promise.reject(error);
        }

        // Si el error es 401 y no es del endpoint de refresh y no hemos intentado refrescar ya
        // Y no estamos en una ruta pública y el refresh no ha fallado previamente
        if (error.response?.status === 401 && !originalRequest._retry && !isPublicApiEndpoint && !isPublicFrontendRoute && !this.refreshFailed) {
          // Si es la petición de refresh, no redirigir (dejar que el componente maneje)
          if (originalRequest.url?.includes('/auth/refresh')) {
            this.refreshFailed = true;
            clearCsrfToken();
            // No redirigir automáticamente, dejar que el componente maneje
            return Promise.reject(error);
          }

          // Si ya estamos refrescando, encolar la petición
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                // Las cookies se envían automáticamente, solo agregar CSRF si es necesario
                addCsrfTokenToHeaders(originalRequest.headers, originalRequest.method);
                return this.api.request(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Refresh token ahora se lee de cookies automáticamente
            const response = await this.refreshToken();
            
            // Si el refresh fue exitoso, resetear el flag
            this.refreshFailed = false;
            
            // Actualizar token CSRF del header de respuesta usando helper
            updateCsrfTokenFromHeaders(response.headers);
              
            // Procesar cola de peticiones fallidas
            this.processQueue(null);
              
            // Reintentar petición original (las cookies se envían automáticamente)
            addCsrfTokenToHeaders(originalRequest.headers, originalRequest.method);
            return this.api.request(originalRequest);
          } catch (refreshError) {
            // Si falla el refresh, marcar como fallido y limpiar
            this.refreshFailed = true;
            this.processQueue(refreshError);
            clearCsrfToken();
            // No redirigir automáticamente para evitar bucles
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }
        // Los errores 403 (Forbidden) no deben redirigir al login,
        // se manejan en el componente para mostrar el mensaje de error
        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    this.failedQueue = [];
  }

  // Auth endpoints
  async register(data: RegisterRequest): Promise<{ id: string; email: string; is_verified: boolean }> {
    const response = await this.api.post('/auth/register', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<TokenResponse> {
    const response = await this.api.post('/auth/login', data);
    // Los tokens ahora están en cookies httpOnly, no en localStorage
    // Actualizar token CSRF del header de respuesta usando helper
    updateCsrfTokenFromHeaders(response.headers);
    
    // Resetear flag de refresh fallido al hacer login exitoso
    this.refreshFailed = false;
    
    // Obtener access token del header como fallback para móviles
    // donde las cookies cross-origin pueden no funcionar correctamente
    const accessTokenFromHeader = response.headers['x-access-token'];
    if (accessTokenFromHeader) {
      accessTokenMemory = accessTokenFromHeader;
    }
    
    // La respuesta ahora incluye los datos del usuario para evitar segunda petición
    return response.data;
  }

  async refreshToken(): Promise<any> {
    // No intentar refresh si estamos en rutas públicas
    const currentPath = window.location.pathname;
    if (isPublicRoute(currentPath)) {
      throw new Error('Cannot refresh token on public routes');
    }
    
    // Refresh token ahora se lee de cookies automáticamente
    const response = await axios.post(
      `${API_URL}/auth/refresh`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // Enviar cookies
      }
    );
    // Actualizar token CSRF del header usando helper
    updateCsrfTokenFromHeaders(response.headers);
    
    // Actualizar access token del header como fallback
    const accessTokenFromHeader = response.headers['x-access-token'];
    if (accessTokenFromHeader) {
      accessTokenMemory = accessTokenFromHeader;
    }
    
    return response;
  }

  async logout(): Promise<void> {
    // Logout ahora lee refresh token de cookies automáticamente
    await this.api.post('/auth/logout', {});
    // Limpiar tokens de memoria usando helpers
    clearCsrfToken();
    accessTokenMemory = null;
    // Resetear flag de refresh fallido al hacer logout
    this.refreshFailed = false;
  }

  async deleteAccount(): Promise<void> {
    // Eliminar cuenta de usuario (Derecho al Olvido - GDPR)
    await this.api.delete('/auth/delete-account');
    // Limpiar tokens de memoria
    clearCsrfToken();
    accessTokenMemory = null;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/auth/me');
    // El interceptor de respuestas actualizará el token CSRF automáticamente
    // pero verificamos que se haya actualizado para asegurar que esté disponible
    if (!getCsrfToken() && response.headers['x-csrf-token']) {
      // Si por alguna razón no se actualizó, actualizarlo manualmente
      updateCsrfTokenFromHeaders(response.headers);
    }
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.api.put('/auth/me', data);
    return response.data;
  }

  async updateBackgroundImage(bgId: string): Promise<User> {
    const response = await this.api.put('/auth/me', { background_image: bgId });
    return response.data;
  }

  async resetPassword(newPassword: string): Promise<{ message: string }> {
    const response = await this.api.post('/auth/reset-password', { new_password: newPassword });
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.api.post('/auth/change-password', { 
      current_password: currentPassword,
      new_password: newPassword 
    });
    return response.data;
  }

  async needsFirstAdmin(): Promise<{ needs: boolean }> {
    const response = await this.api.get('/auth/needs-first-admin');
    return response.data;
  }

  async getSetupToken(): Promise<{ setup_token: string }> {
    // Obtener token de inicialización
    const response = await axios.get(`${API_URL}/auth/setup-token`);
    return response.data;
  }

  async createFirstAdmin(data: RegisterRequest, setupToken: string): Promise<{ id: string; email: string; message: string }> {
    // Este endpoint requiere token de inicialización
    const response = await axios.post(
      `${API_URL}/auth/create-first-admin`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Setup-Token': setupToken,
        },
      }
    );
    return response.data;
  }

  // Admin endpoints - Users
  async getUsers(params?: {
    q?: string;
    page?: number;
    per_page?: number;
    role_id?: string;
    is_active?: boolean;
  }): Promise<PaginatedResponse<User>> {
    const response = await this.api.get('/admin/users', { params });
    return response.data;
  }

  async getUser(userId: string): Promise<User> {
    const response = await this.api.get(`/admin/users/${userId}`);
    return response.data;
  }

  async createUser(data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    role_id: string;
    locale?: string;
    avatar_url?: string;
    background_image?: string;
  }): Promise<User> {
    const response = await this.api.post('/admin/users', data);
    return response.data;
  }

  async updateUser(userId: string, data: Partial<User>): Promise<User> {
    const response = await this.api.put(`/admin/users/${userId}`, data);
    return response.data;
  }

  async deactivateUser(userId: string, reason?: string): Promise<{ status: string }> {
    const response = await this.api.post(`/admin/users/${userId}/deactivate`, { reason });
    return response.data;
  }

  async reactivateUser(userId: string): Promise<{ status: string }> {
    const response = await this.api.post(`/admin/users/${userId}/reactivate`);
    return response.data;
  }

  async promoteUserToAdmin(userId: string): Promise<User> {
    const response = await this.api.post(`/admin/users/${userId}/promote-to-admin`);
    return response.data;
  }

  async forcePasswordReset(userId: string, newPassword: string): Promise<{ status: string }> {
    const response = await this.api.post(`/admin/users/${userId}/force-password-reset`, {
      new_password: newPassword,
    });
    return response.data;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/admin/users/${userId}`);
  }

  // Admin endpoints - Roles
  async getRoles(): Promise<Role[]> {
    const response = await this.api.get('/admin/roles');
    return response.data;
  }

  async createRole(data: {
    name: string;
    description?: string;
    permission_ids: string[];
  }): Promise<Role> {
    const response = await this.api.post('/admin/roles', data);
    return response.data;
  }

  async updateRole(roleId: string, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    permission_ids?: string[];
  }): Promise<Role> {
    const response = await this.api.put(`/admin/roles/${roleId}`, data);
    return response.data;
  }

  async deleteRole(roleId: string, newRoleId: string): Promise<{ status: string; message: string; users_migrated: number }> {
    const response = await this.api.delete(`/admin/roles/${roleId}?new_role_id=${newRoleId}`);
    return response.data;
  }

  async getRoleUsers(roleId: string): Promise<User[]> {
    const response = await this.api.get(`/admin/roles/${roleId}/users`);
    return response.data;
  }

  async getPermissions(): Promise<Permission[]> {
    const response = await this.api.get('/admin/permissions');
    return response.data;
  }

  async getRoleTabs(roleId: string): Promise<Tab[]> {
    const response = await this.api.get(`/admin/roles/${roleId}/tabs`);
    return response.data;
  }

  async updateRoleTabs(roleId: string, tabIds: string[]): Promise<{ status: string; tab_ids: string[] }> {
    const response = await this.api.put(`/admin/roles/${roleId}/tabs`, { tab_ids: tabIds });
    return response.data;
  }

  // Tabs endpoints
  async getTabs(): Promise<Tab[]> {
    const response = await this.api.get('/tabs/');
    return response.data;
  }

  async getAllTabs(): Promise<Tab[]> {
    const response = await this.api.get('/tabs/all');
    return response.data;
  }

  async createTab(data: {
    name: string;
    label: string;
    path: string;
    icon?: string;
    order?: number;
    description?: string;
    role_ids: string[];
  }): Promise<Tab> {
    const response = await this.api.post('/tabs/', data);
    return response.data;
  }

  async updateTab(tabId: string, data: {
    name?: string;
    label?: string;
    path?: string;
    icon?: string;
    order?: number;
    is_active?: boolean;
    description?: string;
    role_ids?: string[];
  }): Promise<Tab> {
    const response = await this.api.put(`/tabs/${tabId}`, data);
    return response.data;
  }

  async deleteTab(tabId: string): Promise<void> {
    await this.api.delete(`/tabs/${tabId}`);
  }

  // Proyectos endpoints
  async getProyectos(): Promise<Proyecto[]> {
    const response = await this.api.get('/proyectos');
    return response.data;
  }

  async getProyecto(proyectoId: string): Promise<Proyecto> {
    const response = await this.api.get(`/proyectos/${proyectoId}`);
    return response.data;
  }

  async createProyecto(data: {
    nombre: string;
    descripcion?: string | null;
    codigo_grant?: string | null;
  }): Promise<Proyecto> {
    const response = await this.api.post('/proyectos', data);
    return response.data;
  }

  async updateProyecto(
    proyectoId: string,
    data: {
      nombre?: string;
      descripcion?: string | null;
      codigo_grant?: string | null;
    }
  ): Promise<Proyecto> {
    const response = await this.api.put(`/proyectos/${proyectoId}`, data);
    return response.data;
  }

  async deleteProyecto(proyectoId: string): Promise<void> {
    await this.api.delete(`/proyectos/${proyectoId}`);
  }

  // Ensayos endpoints
  async getEnsayos(proyectoId?: string): Promise<Ensayo[]> {
    const params = proyectoId ? { proyecto_id: proyectoId } : {};
    const response = await this.api.get('/ensayos', { params });
    return response.data;
  }

  async getEnsayo(ensayoId: string): Promise<Ensayo> {
    const response = await this.api.get(`/ensayos/${ensayoId}`);
    return response.data;
  }

  async createEnsayo(data: {
    nombre: string;
    fecha_ensayo: string;
    codigo: string;
    objetivos: string;
    incidencias?: string | null;
    conclusiones?: string | null;
    proyecto_id?: string | null;
    tipo_ensayo?: string | null;
  }): Promise<Ensayo> {
    const response = await this.api.post('/ensayos', data);
    return response.data;
  }

  async updateEnsayo(
    ensayoId: string,
    data: {
      nombre?: string;
      fecha_ensayo?: string;
      codigo?: string;
      objetivos?: string;
      incidencias?: string | null;
      conclusiones?: string | null;
      proyecto_id?: string | null;
      cte_substrate?: number | null;
      beam_length?: number | null;
      beam_width?: number | null;
      beam_height?: number | null;
      tipo_ensayo?: string | null;
      displacement_calculation_status?: string | null;
      fos_strain_calculation_status?: string | null;
    }
  ): Promise<Ensayo> {
    const response = await this.api.put(`/ensayos/${ensayoId}`, data);
    return response.data;
  }

  async deleteEnsayo(ensayoId: string): Promise<void> {
    await this.api.delete(`/ensayos/${ensayoId}`);
  }

  async getDeletedEnsayos(proyectoId?: string): Promise<Ensayo[]> {
    const params = proyectoId ? { proyecto_id: proyectoId } : {};
    const response = await this.api.get('/ensayos/deleted/all', { params });
    return response.data;
  }

  async restoreEnsayo(ensayoId: string): Promise<Ensayo> {
    await this.ensureCsrfToken();
    const response = await this.api.post(`/ensayos/${ensayoId}/restore`);
    return response.data;
  }

  async hardDeleteEnsayo(ensayoId: string): Promise<void> {
    await this.ensureCsrfToken();
    await this.api.delete(`/ensayos/${ensayoId}/hard`);
  }

  async uploadActuadorFile(ensayoId: string, file: File): Promise<Ensayo> {
    await this.ensureCsrfToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(
      `/ensayos/${ensayoId}/upload-actuador`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async uploadSensoresFile(ensayoId: string, file: File): Promise<Ensayo> {
    await this.ensureCsrfToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(
      `/ensayos/${ensayoId}/upload-sensores`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  private async ensureCsrfToken(): Promise<void> {
    // Si no tenemos el token CSRF en memoria, hacer una petición GET para obtenerlo
    if (!getCsrfToken()) {
      try {
        // Hacer una petición GET simple para obtener el token CSRF del header de respuesta
        const response = await this.api.get('/auth/me');
        // El interceptor de respuestas actualizará el token automáticamente
        // Pero verificamos que se haya actualizado
        if (!getCsrfToken()) {
          console.error('No se pudo obtener el token CSRF después de /auth/me');
        }
      } catch (error) {
        console.error('Error al obtener token CSRF:', error);
        throw new Error('No se pudo obtener el token CSRF. Por favor, recarga la página.');
      }
    }
  }

  private async downloadFileViaApi(endpoint: string, fallbackFilename: string): Promise<void> {
    const response = await this.api.get(endpoint, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.style.display = 'none';

    const contentDisposition = response.headers['content-disposition'];
    let filename = fallbackFilename;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) filename = filenameMatch[1];
    }
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(link);
  }

  async downloadActuadorFile(ensayoId: string): Promise<void> {
    await this.downloadFileViaApi(`/ensayos/${ensayoId}/download-actuador`, 'actuador.xlsx');
  }

  async downloadSensoresFile(ensayoId: string): Promise<void> {
    await this.downloadFileViaApi(`/ensayos/${ensayoId}/download-sensores`, 'sensores.xlsx');
  }

  async deleteActuadorFile(ensayoId: string): Promise<Ensayo> {
    await this.ensureCsrfToken();
    const response = await this.api.delete(`/ensayos/${ensayoId}/delete-actuador`);
    return response.data;
  }

  async deleteSensoresFile(ensayoId: string): Promise<Ensayo> {
    await this.ensureCsrfToken();
    const response = await this.api.delete(`/ensayos/${ensayoId}/delete-sensores`);
    return response.data;
  }

  async getActuadorData(
    ensayoId: string,
    maxPoints?: number,
    movingAverageWindow?: number
  ): Promise<ActuadorDataList> {
    const params = new URLSearchParams();
    if (maxPoints !== undefined) params.append('max_points', maxPoints.toString());
    if (movingAverageWindow !== undefined) params.append('moving_average_window', movingAverageWindow.toString());
    const queryString = params.toString();
    const url = `/ensayos/${ensayoId}/actuador-data${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getEnsayoSamplingInfo(
    ensayoId: string,
    displayMaxPoints: number = 2000
  ): Promise<EnsayoSamplingInfo> {
    const params = new URLSearchParams();
    params.append('display_max_points', displayMaxPoints.toString());
    const response = await this.api.get(`/ensayos/${ensayoId}/sampling-info?${params.toString()}`);
    return response.data;
  }

  async exportEnsayoData(
    ensayoId: string,
    options: {
      mode: 'all' | 'scatter' | 'temporal';
      useAdjustedTimestamps?: boolean;
      movingAverageWindow?: number;
      variables?: ExportVariableSpec[];
      timeFrom?: number;
      timeTo?: number;
    }
  ): Promise<{ blob: Blob; samplingRateHz?: number; rowCount?: number }> {
    const params = this.buildEnsayoExportQueryParams(options);

    const response = await this.api.get(`/ensayos/${ensayoId}/export-data?${params.toString()}`, {
      responseType: 'blob',
      timeout: 300000,
    });

    const samplingRateHz = response.headers['x-export-sampling-rate-hz'];
    const rowCount = response.headers['x-export-row-count'];

    return {
      blob: new Blob([response.data]),
      samplingRateHz: samplingRateHz ? parseFloat(samplingRateHz) : undefined,
      rowCount: rowCount ? parseInt(rowCount, 10) : undefined,
    };
  }

  buildEnsayoExportQueryParams(options: {
    mode: 'all' | 'scatter' | 'temporal';
    useAdjustedTimestamps?: boolean;
    movingAverageWindow?: number;
    variables?: ExportVariableSpec[];
    timeFrom?: number;
    timeTo?: number;
  }): URLSearchParams {
    const params = new URLSearchParams();
    params.append('mode', options.mode);
    if (options.useAdjustedTimestamps !== undefined) {
      params.append('use_adjusted_timestamps', String(options.useAdjustedTimestamps));
    }
    if (options.movingAverageWindow !== undefined) {
      params.append('moving_average_window', String(options.movingAverageWindow));
    }
    if (options.variables?.length) {
      params.append('variables', JSON.stringify(options.variables));
    }
    if (options.timeFrom !== undefined) {
      params.append('time_from', String(options.timeFrom));
    }
    if (options.timeTo !== undefined) {
      params.append('time_to', String(options.timeTo));
    }
    return params;
  }

  async startEnsayoExportJob(
    ensayoId: string,
    options: Parameters<ApiService['exportEnsayoData']>[1]
  ): Promise<ExportJobStartResponse> {
    await this.ensureCsrfToken();
    const params = this.buildEnsayoExportQueryParams(options);
    const response = await this.api.post(
      `/ensayos/${ensayoId}/export-data/async?${params.toString()}`
    );
    return response.data;
  }

  async getEnsayoExportJobStatus(
    ensayoId: string,
    jobId: string
  ): Promise<ExportJobStatusResponse> {
    const response = await this.api.get(`/ensayos/${ensayoId}/export-jobs/${jobId}`);
    return response.data;
  }

  async downloadEnsayoExportJob(
    ensayoId: string,
    jobId: string,
    fallbackFilename: string
  ): Promise<{ samplingRateHz?: number; rowCount?: number }> {
    const response = await this.api.get(
      `/ensayos/${ensayoId}/export-jobs/${jobId}/download`,
      { responseType: 'blob', timeout: 120000 }
    );
    const samplingRateHz = response.headers['x-export-sampling-rate-hz'];
    const rowCount = response.headers['x-export-row-count'];
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.style.display = 'none';
    link.download = fallbackFilename;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(link);
    return {
      samplingRateHz: samplingRateHz ? parseFloat(samplingRateHz) : undefined,
      rowCount: rowCount ? parseInt(rowCount, 10) : undefined,
    };
  }

  async downloadEnsayoExport(
    ensayoId: string,
    options: Parameters<ApiService['exportEnsayoData']>[1],
    fallbackFilename: string
  ): Promise<{ samplingRateHz?: number; rowCount?: number }> {
    const { blob, samplingRateHz, rowCount } = await this.exportEnsayoData(ensayoId, options);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.style.display = 'none';
    link.download = fallbackFilename;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(link);
    return { samplingRateHz, rowCount };
  }

  async processActuadorFile(ensayoId: string): Promise<{ message: string; ensayo_id: string }> {
    await this.ensureCsrfToken();
    const response = await this.api.post(`/ensayos/${ensayoId}/process-actuador`);
    return response.data;
  }

  // Métodos para sensores
  async getSensores(ensayoId: string): Promise<SensorList> {
    const response = await this.api.get(`/ensayos/${ensayoId}/sensores`);
    return response.data;
  }

  async getSensor(ensayoId: string, sensorId: string): Promise<SensorWithData> {
    const response = await this.api.get(`/ensayos/${ensayoId}/sensores/${sensorId}`);
    return response.data;
  }

  async getSensorData(
    ensayoId: string,
    sensorId: string,
    limit?: number,
    offset?: number,
    maxPoints?: number,
    movingAverageWindow?: number,
    useAdjustedTimestamps?: boolean
  ): Promise<SensorDataList> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (maxPoints !== undefined) params.append('max_points', maxPoints.toString());
    if (movingAverageWindow !== undefined) params.append('moving_average_window', movingAverageWindow.toString());
    if (useAdjustedTimestamps !== undefined) params.append('use_adjusted_timestamps', useAdjustedTimestamps.toString());
    const response = await this.api.get(`/ensayos/${ensayoId}/sensores/${sensorId}/datos?${params.toString()}`);
    return response.data;
  }

  async getSensorDataBatch(
    ensayoId: string,
    sensorIds: string[],
    options?: {
      maxPoints?: number;
      movingAverageWindow?: number;
      useAdjustedTimestamps?: boolean;
    }
  ): Promise<SensorDataBatchResponse> {
    const response = await this.api.post(`/ensayos/${ensayoId}/sensores/datos-batch`, {
      sensor_ids: sensorIds,
      max_points: options?.maxPoints ?? 2000,
      moving_average_window: options?.movingAverageWindow,
      use_adjusted_timestamps: options?.useAdjustedTimestamps ?? false,
    });
    return response.data;
  }

  async getScatterDisplayData(
    ensayoId: string,
    options: {
      xVariableId: string;
      xSensorId: string;
      yVariableId: string;
      ySensorId: string;
      maxDisplayPoints?: number;
      useAdjustedTimestamps?: boolean;
      movingAverageWindow?: number;
      timeFrom?: number;
      timeTo?: number;
    }
  ): Promise<ScatterDisplayResponse> {
    const params = new URLSearchParams();
    params.append('x_variable_id', options.xVariableId);
    params.append('x_sensor_id', options.xSensorId);
    params.append('y_variable_id', options.yVariableId);
    params.append('y_sensor_id', options.ySensorId);
    if (options.maxDisplayPoints !== undefined) {
      params.append('max_display_points', String(options.maxDisplayPoints));
    }
    if (options.useAdjustedTimestamps !== undefined) {
      params.append('use_adjusted_timestamps', String(options.useAdjustedTimestamps));
    }
    if (options.movingAverageWindow !== undefined) {
      params.append('moving_average_window', String(options.movingAverageWindow));
    }
    if (options.timeFrom !== undefined) params.append('time_from', String(options.timeFrom));
    if (options.timeTo !== undefined) params.append('time_to', String(options.timeTo));

    const response = await this.api.get(
      `/ensayos/${ensayoId}/scatter-data?${params.toString()}`,
      { timeout: 120000 }
    );
    return response.data;
  }

  async updateSensor(ensayoId: string, sensorId: string, data: Partial<Sensor>): Promise<Sensor> {
    await this.ensureCsrfToken();
    const response = await this.api.patch(`/ensayos/${ensayoId}/sensores/${sensorId}`, data);
    return response.data;
  }

  async asociarSensorFOS(ensayoId: string, sensorId: string, fosSensorId: string): Promise<Sensor> {
    await this.ensureCsrfToken();
    const response = await this.api.post(`/ensayos/${ensayoId}/sensores/${sensorId}/asociar-fos?fos_sensor_id=${encodeURIComponent(fosSensorId)}`);
    return response.data;
  }

  async desasociarSensorFOS(ensayoId: string, sensorId: string): Promise<Sensor> {
    await this.ensureCsrfToken();
    const response = await this.api.delete(`/ensayos/${ensayoId}/sensores/${sensorId}/asociar-fos`);
    return response.data;
  }

  async processSensoresFile(ensayoId: string): Promise<{ message: string; ensayo_id: string }> {
    await this.ensureCsrfToken();
    const response = await this.api.post(`/ensayos/${ensayoId}/process-sensores`);
    return response.data;
  }

  async calcularDesplazamientosLVDT(ensayoId: string): Promise<{
    message: string;
    status: string;
    ensayo_id: string;
  }> {
    await this.ensureCsrfToken();
    // Esta operación puede tardar varios minutos, aumentar timeout a 5 minutos
    const response = await this.api.post(
      `/ensayos/${ensayoId}/sensores/calcular-desplazamientos`,
      {},
      { timeout: 300000 } // 5 minutos
    );
    return response.data;
  }

  async calcularStrainsFOS(ensayoId: string, simplified: boolean = false): Promise<{
    message: string;
    status: string;
    ensayo_id: string;
    simplified: boolean;
  }> {
    await this.ensureCsrfToken();
    // Esta operación puede tardar varios minutos, aumentar timeout a 5 minutos
    const response = await this.api.post(
      `/ensayos/${ensayoId}/sensores/calcular-strains-fos?simplified=${simplified}`,
      {},
      { timeout: 300000 } // 5 minutos
    );
    return response.data;
  }

  // ============================================
  // MÉTODOS PARA AJUSTE DE TIMESTAMPS
  // ============================================

  async createTimestampAdjustment(
    ensayoId: string,
    request: TimestampAdjustmentRequest
  ): Promise<TimestampAdjustment> {
    await this.ensureCsrfToken();
    const response = await this.api.post(`/ensayos/${ensayoId}/timestamp-adjustments`, request, {
      timeout: 120000,
    });
    return response.data;
  }

  async getTimestampAdjustments(
    ensayoId: string,
    statusFilter?: string
  ): Promise<TimestampAdjustmentList> {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    const queryString = params.toString();
    const url = `/ensayos/${ensayoId}/timestamp-adjustments${queryString ? `?${queryString}` : ''}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getTimestampAdjustment(
    ensayoId: string,
    adjustmentId: string
  ): Promise<TimestampAdjustment> {
    const response = await this.api.get(`/ensayos/${ensayoId}/timestamp-adjustments/${adjustmentId}`);
    return response.data;
  }

  async updateFineOffset(
    ensayoId: string,
    adjustmentId: string,
    fineOffsetSeconds: number
  ): Promise<TimestampAdjustment> {
    await this.ensureCsrfToken();
    const url = `/ensayos/${ensayoId}/timestamp-adjustments/${adjustmentId}/fine-offset`;
    const body = { fine_offset_seconds: fineOffsetSeconds };
    const config = { timeout: 60000 };

    let lastError: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await this.api.put(url, body, config);
        return response.data;
      } catch (e: unknown) {
        lastError = e;
        const ax = e as AxiosError;
        const noResponse = !ax.response;
        const transient =
          noResponse &&
          (ax.code === 'ERR_NETWORK' ||
            ax.code === 'ECONNABORTED' ||
            ax.message === 'Network Error');
        if (!transient || attempt === 2) {
          throw e;
        }
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    throw lastError;
  }

  // ============================================
  // MÉTODOS PARA ARCHIVOS MULTIMEDIA
  // ============================================

  async uploadMediaFiles(ensayoId: string, files: File[], descriptions?: string[]): Promise<EnsayoMedia[]> {
    await this.ensureCsrfToken();
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (descriptions) {
      descriptions.forEach((desc) => formData.append('descriptions', desc || ''));
    }
    const response = await this.api.post(
      `/ensayos/${ensayoId}/upload-media`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async updateMediaDescription(ensayoId: string, mediaId: string, description: string | null): Promise<EnsayoMedia> {
    await this.ensureCsrfToken();
    const formData = new FormData();
    if (description) formData.append('description', description);
    const response = await this.api.patch(
      `/ensayos/${ensayoId}/media/${mediaId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async getEnsayoMedia(ensayoId: string): Promise<EnsayoMediaList> {
    const response = await this.api.get(`/ensayos/${ensayoId}/media`);
    return response.data;
  }

  getMediaFileUrl(ensayoId: string, mediaId: string): string {
    return `${API_URL}/ensayos/${ensayoId}/media/${mediaId}`;
  }

  /**
   * Obtener un archivo multimedia como blob (para asegurar que se envíen las cookies de autenticación)
   */
  async getMediaFileAsBlob(ensayoId: string, mediaId: string): Promise<Blob> {
    const response = await this.api.get(
      `/ensayos/${ensayoId}/media/${mediaId}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async deleteMediaFile(ensayoId: string, mediaId: string): Promise<void> {
    await this.ensureCsrfToken();
    await this.api.delete(`/ensayos/${ensayoId}/media/${mediaId}`);
  }

  // ============================================
  // MÉTODOS PARA SENSORES FOS
  // ============================================

  async getFOSSensors(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    tipologia?: string;
    product_type?: string;
    is_active?: boolean;
  }): Promise<FOSSensorList> {
    const response = await this.api.get('/fos-sensors', { params });
    return response.data;
  }

  async getFOSSensor(sensorId: string): Promise<FOSSensor> {
    const response = await this.api.get(`/fos-sensors/${sensorId}`);
    return response.data;
  }

  async createFOSSensor(data: Omit<FOSSensor, 'id' | 'created_at' | 'updated_at'>): Promise<FOSSensor> {
    await this.ensureCsrfToken();
    const response = await this.api.post('/fos-sensors', data);
    return response.data;
  }

  async updateFOSSensor(sensorId: string, data: Partial<FOSSensor>): Promise<FOSSensor> {
    await this.ensureCsrfToken();
    const response = await this.api.put(`/fos-sensors/${sensorId}`, data);
    return response.data;
  }

  async deleteFOSSensor(sensorId: string): Promise<void> {
    await this.ensureCsrfToken();
    await this.api.delete(`/fos-sensors/${sensorId}`);
  }

  // Documents (PDF nomenclatura sensores)
  async getNomenclaturaPdfInfo(): Promise<{ url: string | null; has_document: boolean }> {
    const response = await this.api.get('/documents/nomenclatura-sensores');
    return response.data;
  }

  async streamNomenclaturaPdf(): Promise<Blob> {
    const response = await this.api.get('/documents/nomenclatura-sensores/file', {
      responseType: 'blob',
    });
    return response.data;
  }

  async uploadNomenclaturaPdf(file: File): Promise<{ url: string; status: string }> {
    await this.ensureCsrfToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/documents/nomenclatura-sensores', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteNomenclaturaPdf(): Promise<{ status: string }> {
    await this.ensureCsrfToken();
    const response = await this.api.delete('/documents/nomenclatura-sensores');
    return response.data;
  }
}

export const apiService = new ApiService();

